/**
 * `/session/$sessionId` — what the call left behind.
 *
 * The screen the whole product is for. Its shape is one idea: **playback time
 * is the spine.** The player owns the top and stays there; the transcript
 * scrolls with it and seeks it; the activity ribbon is indexed by it; the
 * insights are the same conversation summarised. There is exactly one clock on
 * this page and everything reads from it.
 *
 * Addressed by session id rather than join code, the opposite choice from
 * `/call/$code` — a join code is short-lived, reusable and guessable, and a
 * link to a finished recording should be none of those.
 *
 * It renders for FOUR kinds of session, and the ones that are not "finished
 * and perfect" are the common case:
 *
 *   1. complete — audio, transcript, metrics. The showcase.
 *   2. still processing — the state a call is in for the first few minutes
 *      after it ends. Everything says "not yet", nothing says "error", and the
 *      page rechecks by itself until it settles.
 *   3. never recorded, failed, or purged by retention — permanent absences,
 *      each with its own explanation rather than a shared shrug.
 *   4. not created by this app — no join code and no human names, served live
 *      from the control plane. Everything else still works.
 *
 * ===========================================================================
 * THE SHAPE ON A PHONE, AND WHY IT IS THIS ONE
 *
 * Four things want to be on screen at once — the player, the transcript, the
 * speaker rail and the metrics — and on a 375x667 viewport only two of them
 * fit. Rather than stack all four into a 13,000px page and call it responsive,
 * the screen is cut into a PINNED SPINE and one scrolling body:
 *
 *     [ scrolls away ]  the header: back, title, one line of facts
 *     [ pinned       ]  the player — ribbon, transport, speed
 *     [ pinned       ]  Transcript / Insights / Files
 *     [ scrolls      ]  the active panel
 *
 * The player is pinned because it is the thing you operate WHILE reading: you
 * scrub, you skip back ten seconds, you slow it down, and every one of those is
 * a thing you do with your eyes on the transcript. A player you have to scroll
 * back to is a player you stop using, and then the transcript is just a text
 * file. It is the one part of this screen that is a control rather than a
 * document, so it is the one part that does not move.
 *
 * The rail and the metrics are NOT pinned and not stacked: the rail's three
 * jobs are split (its filter becomes a chip row inside the transcript; its
 * legend and talk-share summary are the Insights tab's bars, which are the same
 * data from the same roster), and the metrics stay behind their tab. Nothing is
 * dropped; two things move to where there is room for them.
 *
 * The header is deliberately NOT pinned. It is orientation, not control — you
 * read it on arrival and never again — and 180px of permanent chrome to keep
 * telling you the title of the recording you are listening to is exactly the
 * trade that makes a web app feel like a web page.
 *
 * It is `position: sticky` inside `Shell`'s single scroller, never `fixed`.
 * Fixed chrome over a scrolling document is where most of iOS's "it's close but
 * it doesn't fit" comes from: it has to be re-pinned by hand every time the URL
 * bar collapses or the keyboard opens, and it needs `env(safe-area-inset-*)`
 * arithmetic to stay clear of the home indicator. Sticky needs none of that —
 * the scroller already ends above the tab bar, which already ends above the
 * home indicator, so anything pinned inside it is clear of both by
 * construction. That is also why the "jump to what is playing" pill in
 * `TranscriptPanel` is `sticky bottom-3` rather than a floating button.
 *
 * The pinned block is NOT a second scroller. `Shell` owns the only one, and a
 * scroller inside a scroller on a touch screen is a trap: a flick that starts
 * on the transcript scrolls the inner one to its end and then stops dead
 * instead of carrying the page.
 * ===========================================================================
 */

import * as React from 'react'
import { useParams } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { FileText } from 'lucide-react'
import { isSessionAligned } from '@era/shared'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { artifactUrl } from '@/lib/api'
import { formatRelative } from '@/lib/format'
import { keys } from '@/lib/query'
import { cn } from '@/lib/utils'
import {
  useActivity,
  useSessionDetail,
  useSessionVideo,
  useTranscript,
} from '@/features/session/api'
import { SPINE_ATTR, findScroller, spineHeadroom } from '@/features/session/layout'
import { FilesPanel } from '@/features/session/components/files-panel'
import { InsightsPanel } from '@/features/session/components/insights-panel'
import { MediaQualityPanel } from '@/features/session/components/media-quality-panel'
import { PlayerCard, type PlayerVideo } from '@/features/session/components/player-card'
import { SessionHeader } from '@/features/session/components/session-header'
import { SpeakerRail } from '@/features/session/components/speaker-rail'
import {
  Notice,
  PendingBlock,
  SessionFailure,
  SessionSkeleton,
  WaitingCard,
} from '@/features/session/components/states'
import type { TimelineLane } from '@/features/session/components/timeline'
import { TranscriptPanel } from '@/features/session/components/transcript-panel'
import { buildRoster } from '@/features/session/speakers'
import { useAudioPlayer } from '@/features/session/use-player'
import {
  deriveVideoState,
  lanesOnMediaTimeline,
  mediaTimelineOffsetMs,
  playerOnDocumentTimeline,
} from '@/features/session/video'

/**
 * True once `ready` has gone false → true with this page already open.
 *
 * Deliberately NOT "is ready". Two things have to be told apart and they look
 * identical from inside a component:
 *
 *   - a finished session OPENED, whose transcript query goes pending → ready
 *     in the first second because it is being fetched. Not an event.
 *   - a running session that FINISHED while somebody watched. The event.
 *
 * So `null` means "nothing is known yet" and is skipped entirely, and the
 * first real value is recorded as the state the page opened in rather than
 * compared against anything. Only a later false → true is an arrival.
 *
 * Getting this wrong is not cosmetic. The first version keyed on the
 * transcript query's own status, which is `pending` until the detail response
 * says there is a pointer to fetch — so it announced "the transcript just
 * finished" on every completed session anybody opened. Caught in a screenshot
 * of a session two hours old.
 */
function useArrivedWhileOpen(ready: boolean | null): boolean {
  const previous = React.useRef<boolean | null>(null)
  const [arrived, setArrived] = React.useState(false)

  React.useEffect(() => {
    if (ready === null) return
    const before = previous.current
    previous.current = ready
    if (before === null) return
    if (!before && ready) setArrived(true)
  }, [ready])

  return arrived
}

export function SessionDetailRoute() {
  const { sessionId } = useParams({ from: '/session/$sessionId' })
  const queryClient = useQueryClient()

  const detail = useSessionDetail(sessionId)
  const view = detail.data ?? null

  const playback = view?.artifacts.find((artifact) => artifact.kind === 'playback') ?? null
  const hasActivityArtifact = Boolean(
    view?.artifacts.some((artifact) => artifact.kind === 'activity'),
  )

  // Both documents are only asked for once their pointer exists. Fetching
  // speculatively would mean a guaranteed 409 on every freshly ended call and a
  // red error in the network panel for a state that is entirely normal.
  const transcript = useTranscript(sessionId, Boolean(view?.transcript.artifact_id))
  const activity = useActivity(sessionId, hasActivityArtifact)

  const transcriptDocument = transcript.status === 'ready' ? transcript.document : null
  const activityDocument = activity.status === 'ready' ? activity.document : null

  /* -- one roster, read by every panel -------------------------------------- */
  //
  // `participants[]` fixes the order and therefore the colours. Identities that
  // exist only in a document are appended, never inserted, so nobody's colour
  // changes when the transcript finishes loading.
  const roster = React.useMemo(() => {
    const extras: string[] = []
    for (const speaker of transcriptDocument?.speakers ?? []) extras.push(speaker)
    for (const speaker of activityDocument?.speakers ?? []) extras.push(speaker.identity)
    for (const identity of Object.keys(view?.metrics?.talk_pct ?? {})) extras.push(identity)
    for (const connection of view?.media_quality?.connections ?? []) {
      extras.push(connection.observer_identity)
      for (const track of connection.tracks) extras.push(track.subject_identity)
    }
    return buildRoster(view?.participants ?? [], extras)
  }, [view?.participants, view?.metrics, view?.media_quality, transcriptDocument, activityDocument])

  /* -- retained video -------------------------------------------------------- */
  //
  // A SEPARATE REQUEST, and it has to be. `composite_ready` invalidates it and
  // `video_pipeline.render` supplies authoritative current status. The cached
  // detail view cannot carry the answer: `isFinalDetail` marks a
  // snapshot final on the AUDIO pipeline alone, so a session freezes its
  // snapshot while its render is still queued and would report "no video"
  // forever. The BFF route says the same thing at more length.
  //
  // Enabled only once the detail response has arrived, so a session that turns
  // out not to exist does not also fire a video request at it.
  const videoQuery = useSessionVideo(sessionId, Boolean(view))
  const videoState = React.useMemo(
    () => deriveVideoState(videoQuery.data ?? null),
    [videoQuery.data],
  )
  const videoReady = videoState.kind === 'ready' ? videoState : null

  const playerVideo: PlayerVideo | null = React.useMemo(
    () =>
      videoReady
        ? {
            render: videoReady.render,
            aspectRatio: videoReady.aspectRatio,
            caveat: videoReady.caveat,
          }
        : null,
    [videoReady],
  )

  /* -- the clock ------------------------------------------------------------ */
  //
  // ONE player, and when there is retained video it is the video.
  //
  // Retained video and the audio/transcript document intentionally have
  // DIFFERENT zeroes. MP4 t=0 is the first video-enabled policy boundary, so a
  // call that enabled video 20 seconds late does not make a viewer sit through
  // 20 seconds of black. Transcript/activity offsets remain relative to the
  // audio timeline. Both origins are published as wall-clock timestamps, so
  // the exact translation is `video_ms = document_ms - (video_t0-audio_t0)`.
  //
  // The media player below stays MP4-relative. `documentPlayer` translates at
  // the transcript/insights boundary, and `playerLanes` translates in the
  // other direction for the MP4 ribbon. Keeping those two adapters explicit is
  // what prevents a seek fix from lying about the media element's real clock.
  const player = useAudioPlayer({
    src: playerVideo
      ? `/api/sessions/${encodeURIComponent(sessionId)}/video`
      : playback
        ? artifactUrl(sessionId, playback.artifact_id)
        : null,
    // The catalog's own length, so the ribbon has a scale before a byte has
    // been fetched — and a permanent fallback if the browser never works one
    // out from the stream. `render.duration_ms` is the video's counterpart to
    // `playback.duration_ms` and is authoritative for the composite: measured
    // equal to the MP4's real length to the millisecond (49,000 ms reported,
    // 49.000 s from ffprobe).
    fallbackDurationMs: playerVideo
      ? (playerVideo.render.duration_ms ?? view?.duration_ms ?? null)
      : (playback?.duration_ms ?? view?.duration_ms ?? null),
  })

  // The spine exists if either final product does. New video policies include
  // audio, but legacy/partial data can still have only one verified artifact;
  // either result must remain playable rather than looking unrecorded.
  const hasPlayer = Boolean(playback) || Boolean(playerVideo)

  const lanes: TimelineLane[] = React.useMemo(
    () =>
      activityDocument?.speakers.map((speaker) => ({
        speaker: roster.get(speaker.identity),
        segments: speaker.segments,
      })) ?? [],
    [activityDocument, roster],
  )

  const activityTimelineStart = React.useMemo(() => {
    const t0Ns = activityDocument?.t0_ns
    if (typeof t0Ns !== 'number' || !Number.isFinite(t0Ns)) return null
    return new Date(t0Ns / 1_000_000).toISOString()
  }, [activityDocument?.t0_ns])

  const documentTimelineStart = transcriptDocument?.timeline_start ?? activityTimelineStart
  const mediaTimelineStart =
    playerVideo?.render.timeline_start ?? videoQuery.data?.timeline_start ?? null
  const documentOffsetMs = playerVideo
    ? mediaTimelineOffsetMs(mediaTimelineStart, documentTimelineStart)
    : 0
  const documentPlayer = React.useMemo(
    () => playerOnDocumentTimeline(player, documentOffsetMs),
    [player, documentOffsetMs],
  )
  const playerLanes = React.useMemo(
    () =>
      playerVideo
        ? lanesOnMediaTimeline(lanes, documentOffsetMs, playerVideo.render.duration_ms ?? null)
        : lanes,
    [documentOffsetMs, lanes, playerVideo],
  )

  /* -- the clock the metrics are on ----------------------------------------- */
  //
  // `silence_pct` is "share of the SESSION TIMELINE with no active speaker",
  // and `timebase: 'session'` is defined as "shares the mixed playback
  // timeline". So the whole that percentage is a share of is the length of the
  // mixed recording — which is NOT `view.duration_ms`, the time the room was
  // open. Measured on the verification call: room 2:44, recording 1:17,
  // because nobody published for the first 68 seconds.
  //
  // Prefer `playback` (it IS the mixed track), then any other session-timebase
  // artifact — the transcript, activity and metrics documents all report the
  // same length — and fall back to the room's duration only when the catalog
  // carries no session-aligned artifact at all, which is the pre-`timebase`
  // snapshot case where there is nothing better to say.
  const timelineDurationMs = React.useMemo(() => {
    if (playback?.duration_ms != null) return playback.duration_ms
    const aligned = view?.artifacts.find(
      (artifact) => isSessionAligned(artifact.timebase) && artifact.duration_ms != null,
    )
    return aligned?.duration_ms ?? view?.duration_ms ?? null
  }, [playback, view?.artifacts, view?.duration_ms])

  const speakingMs = React.useMemo(() => {
    const map = new Map<string, number>()
    for (const speaker of activityDocument?.speakers ?? []) {
      map.set(speaker.identity, speaker.speaking_ms)
    }
    return map
  }, [activityDocument])

  const [focusIdentity, setFocusIdentity] = React.useState<string | null>(null)

  /* -- the transcript finishing while you are watching ----------------------- */
  //
  // It already updates without a reload: `useSessionDetail` polls while the
  // session is unfinished, the transcript pointer appears, that enables
  // `useTranscript`, and the panel swaps itself out. What was missing is that
  // it happened SILENTLY — a reader on the Insights tab, or one who had
  // stopped watching the waiting card, got no signal that the thing they were
  // waiting for had arrived.
  //
  // IT IS NO LONGER A POLL THAT FEEDS THIS. era-control pushes
  // `transcript_ready` to the BFF with the whole document inline; the BFF
  // applies it to SQLite and republishes it on `GET /api/events`, and
  // `lib/live.ts` invalidates this query when the frame lands — measured at
  // ~15 ms end to end, against the ~15 s this used to wait for the next poll.
  // FINDINGS.md #33(c) is closed by that endpoint.
  //
  // The poll survives as a 90-second backstop rather than being deleted, which
  // is the one thing #33 asked for and did not get; `features/session/api.ts`
  // and `lib/live.ts` say why. Nothing about the hook below changes either
  // way — it keys on the DATA, so it does not care whether the data arrived
  // because we asked or because we were told, which is exactly the property
  // that made swapping the transport underneath it a no-op.
  //
  // Keyed on the POINTER — `transcript.artifact_id`, which is null until the
  // pipeline has written the document — and not on the transcript query, whose
  // pending → ready transition is just this page fetching. `null` while the
  // detail response has not arrived at all, so opening a session mid-load is
  // not mistaken for a session finishing.
  const transcriptArrived = useArrivedWhileOpen(
    view ? view.transcript.artifact_id !== null : null,
  )
  const [arrivalDismissed, setArrivalDismissed] = React.useState(false)

  /* -- how tall the pinned player is ----------------------------------------- */
  //
  // Two things need this number and neither can be told it in advance: the
  // phone's tab strip, which pins directly under the player, and the desktop's
  // speaker rail, which pins 1rem below it. Get it wrong and the second element
  // pins UNDERNEATH the first — the player carries `z-30` and neither of the
  // others does, so their headings simply disappear behind it.
  //
  // It was a hard-coded `13.5rem` once, and it was wrong, because the player's
  // height is not a constant: `Timeline` draws one lane PER SPEAKER, so a
  // six-person call's player is ~64px taller than the two-person one the
  // constant was measured against. Measured, so it is right for any roster, any
  // breakpoint, and any future change to the card's padding.
  //
  // Published as a CSS custom property rather than as an inline `top`, because
  // the two consumers want it at different offsets and only at their own
  // breakpoints — `max-sm:top-[var(--era-spine-h)]` and
  // `lg:top-[calc(var(--era-spine-h)+1rem)]`. An inline style would have to
  // encode the breakpoint in JavaScript, which is a second place for a decision
  // Tailwind is already making.
  const spineRef = React.useRef<HTMLDivElement>(null)
  const [spineHeight, setSpineHeight] = React.useState(0)
  React.useLayoutEffect(() => {
    const element = spineRef.current
    if (!element) return
    // `borderBoxSize`, not `contentRect`: the wrapper carries bottom padding on
    // a phone (the gap between the card and the tab strip under it), and
    // `contentRect` excludes padding — the tab strip would pin that many pixels
    // too high and the card's own bottom edge would scroll away behind it.
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSpineHeight(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height)
    })
    observer.observe(element)
    return () => observer.disconnect()
    // `playerVideo` is in here as well as `playback`: the video surface appears
    // asynchronously (its status is a separate poll) and the card grows by ~211px
    // at 375px when it does. Without the re-observe, the tab strip stays pinned
    // at the audio-only height and the bottom of the player scrolls away behind
    // it — the exact failure the hard-coded `13.5rem` used to cause.
  }, [playback, playerVideo])

  // Zero when there is no recording to pin: the waiting card is a message, not
  // a control, and pinning it would hold a paragraph on screen while the reader
  // scrolls past it looking for anything else.
  const spineOffset = hasPlayer ? spineHeight : 0

  /* -- switching tabs while scrolled deep ------------------------------------ */
  //
  // Radix unmounts the inactive panel, so leaving a 40-minute transcript for
  // Insights takes ~12,000px out of the document under a scroll position that
  // was inside it. The browser clamps, and what you get is the BOTTOM of the
  // metrics — the last chart, mid-page, with no indication that you have landed
  // in the middle of something.
  //
  // The marker is a zero-height element sitting immediately before the tab
  // strip. It exists because the strip itself is `sticky` on a phone and a
  // pinned element's `getBoundingClientRect()` reports where it is PINNED, not
  // where it belongs — so the strip cannot be asked where the panel starts. A
  // static sibling can.
  const markerRef = React.useRef<HTMLDivElement>(null)
  const realignToTabs = React.useCallback(() => {
    // After the swap, not before it. Radix changes the panel synchronously with
    // this callback; the browser then clamps `scrollTop` to the new, shorter
    // content, and it is the position AFTER that clamp we have to correct from.
    requestAnimationFrame(() => {
      const marker = markerRef.current
      const scroller = findScroller(marker)
      if (!marker || !scroller) return
      const delta =
        marker.getBoundingClientRect().top -
        (scroller.getBoundingClientRect().top + spineHeadroom(scroller))
      // Only ever scrolls UP, and only when the tabs are already behind you.
      // Scrolling down to meet a control that is comfortably in view is the
      // page moving for its own reasons, which is worse than the problem.
      if (delta < -1) scroller.scrollBy({ top: delta })
    })
  }, [])

  /* -- states before the page ----------------------------------------------- */

  if (detail.isPending) return <SessionSkeleton />
  if (!view) {
    const error = detail.error
    return (
      <SessionFailure
        sessionId={sessionId}
        status={error?.status ?? 0}
        message={error?.message ?? 'The server did not answer.'}
      />
    )
  }

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: keys.session(sessionId) })
  }

  return (
    <div
      className="space-y-5 sm:space-y-8"
      style={{ '--era-spine-h': `${spineOffset}px` } as React.CSSProperties}
    >
      <SessionHeader view={view} />

      {/* Held back until the document is actually rendered below, so the
          sentence is true when it is read: the pointer appearing is upstream
          saying the transcript exists, which is a second or two before this
          screen has it. */}
      {transcriptArrived && !arrivalDismissed && transcript.status === 'ready' ? (
        <Notice
          tone="ok"
          title="The transcript just finished"
          onDismiss={() => setArrivalDismissed(true)}
        >
          It arrived while this page was open and is already below — nothing was reloaded.
          {view.metrics ? ' The conversation metrics came with it.' : ''}
        </Notice>
      ) : null}

      {view.upstream_unavailable ? (
        <Notice tone="warn" title="Showing what this app stored, not what the platform says">
          The control plane could not be reached, or has forgotten this session. Everything below
          came from this app&rsquo;s own database and was last refreshed{' '}
          {formatRelative(view.fetched_at)}. Playback and downloads will not work while it is
          unreachable — the bytes live upstream.
        </Notice>
      ) : null}

      {/* -- retained video that is not (yet) playable ---------------------- */}
      {/*
        Only `pending` and `failed` get a banner. The other three are silent on
        purpose:

          absent       576 of the 600 sessions in the control plane never kept
                       video. A banner saying so on every one of them is noise
                       that trains people to ignore banners. The Files tab
                       states it plainly, which is where the question is asked.
          unavailable  the page already carries an "upstream is unreachable"
                       banner directly above; a second one about video is the
                       same news twice.
          ready        it is on screen.

        `pending` is `ok`-toned rather than `warn`. It is the healthy state of a
        call that ended a minute ago, and the sentence has to reassure rather
        than alarm — but it must still be SAID while the render is pending.
      */}
      {videoState.kind === 'pending' || videoState.kind === 'failed' ? (
        <Notice
          tone={videoState.kind === 'failed' ? 'warn' : 'ok'}
          title={videoState.title}
        >
          {videoState.detail}
          {videoState.kind === 'pending'
            ? ' The player appears when the completed-render notification arrives; a slow backstop read covers a missed event.'
            : ''}
        </Notice>
      ) : null}

      {view.state === 'partial' ? (
        <Notice tone="warn" title="Some of this call could not be processed">
          {view.pipeline.analyzed_sources ?? 0} of {view.pipeline.expected_sources ?? 0} speaker
          tracks were analysed. The transcript below is real but incomplete — a missing voice is a
          missing track, not a silent participant.
        </Notice>
      ) : null}

      {/* -- the pinned spine, part one: the player ------------------------- */}
      {/*
        The wrapper is what sticks, not the `Card` inside it. The card cannot do
        both jobs: it has to be MEASURED, and `ui/card.tsx` carries a comment
        about why a measuring wrapper around a sticky box stops it sticking (the
        wrapper becomes its containing block, and a wrapper exactly as tall as
        its child leaves nowhere to travel). Moving the stickiness out resolves
        that, and it is also what lets the phone pin the player and the tab
        strip as one block.

        `-mx-4 px-4 bg-paper` bleeds the pinned background to both screen edges,
        against `Shell`'s 16px content inset. Without it the transcript scrolls
        up through two 16px columns of nothing either side of the card, which
        reads as a rendering fault rather than as a layout.

        `lg:top-0`, not the `lg:top-16` it used to be. That 16 was the app
        header's height, correct while the header was `sticky` OVER the
        scrolling document; the header is now a flex SIBLING of the scroller and
        overlaps nothing, so the offset opened a 64px band between the top of
        the scrollport and the pinned player, and transcript rows were plainly
        visible sliding through it. Measured, not guessed: the scroller's own
        top was at y=65 and the pinned card at y=129.
      */}
      <div
        ref={spineRef}
        {...(hasPlayer ? { [SPINE_ATTR]: '' } : {})}
        className={cn(
          hasPlayer &&
            // `pt-2` is not decoration: pinned at `top-0` with no padding, the
            // card's rounded top corners sit exactly on the edge of the
            // scrollport and read as a card that has been cut off rather than
            // one that is floating. 8px of paper above it is what makes it look
            // deliberate, and it is paper the transcript still scrolls behind.
            'max-sm:sticky max-sm:top-0 max-sm:z-30 max-sm:-mx-4 max-sm:bg-paper max-sm:px-4',
            'max-sm:pt-2 max-sm:pb-2',
          // Deliberately not sticky between `sm` and `lg`. A landscape phone is
          // 844px wide and ~390px tall: there, a pinned player is over a third
          // of the viewport and the transcript is a letterbox. Tablets get the
          // stacked layout they had.
          hasPlayer && 'lg:sticky lg:top-0 lg:z-30',
        )}
      >
        {hasPlayer ? (
          <PlayerCard
            player={player}
            sessionId={sessionId}
            artifact={playback}
            video={playerVideo}
            lanes={playerLanes}
            hasActivity={lanes.length > 0}
          />
        ) : (
          <WaitingCard view={view} onRefresh={refresh} refreshing={detail.isFetching} />
        )}
      </div>

      <div className="grid gap-5 sm:gap-8 lg:grid-cols-[248px_1fr] lg:items-start">
        {/* The rail is gone below `sm` — not hidden as an afterthought, but
            because its three jobs were re-homed: the filter is the chip row in
            `TranscriptPanel`, and the legend and talk-share summary are the
            Insights tab's bars, off the same roster and the same colours. At
            375px the rail alone was ~260px above the transcript.
            `display: none` removes it as a grid item too, so the single-column
            rows below `lg` do not leave a gap where it was. */}
        <aside className="hidden sm:block lg:sticky lg:top-[calc(var(--era-spine-h)_+_1rem)]">
          <SpeakerRail
            roster={roster}
            metrics={view.metrics}
            speakingMs={speakingMs}
            focusIdentity={focusIdentity}
            onFocus={setFocusIdentity}
          />
        </aside>

        <Tabs defaultValue="transcript" onValueChange={realignToTabs} className="min-w-0">
          {/* Zero-height, static, and immediately before the strip: the only
              thing on the page that can still say where the panel BEGINS once
              the strip itself is pinned. See `realignToTabs`. */}
          <div ref={markerRef} className="h-0" aria-hidden />

          {/* -- the pinned spine, part two: the tabs ----------------------- */}
          {/* Pinned flush under the player, at whatever height the player
              turned out to be. The wrapper carries the sticking and the bleed
              so `ui/tabs.tsx` — which belongs to the whole app — needs no
              change and no ref. */}
          <div
            {...{ [SPINE_ATTR]: '' }}
            className={cn(
              'max-sm:sticky max-sm:top-[var(--era-spine-h,0px)] max-sm:z-20',
              // The bleed is on the WRAPPER and the inset is on the list, so
              // the list's hairline — the only thing separating pinned chrome
              // from the text sliding under it — runs the full width of the
              // screen while the labels stay in line with everything else.
              'max-sm:-mx-4 max-sm:bg-paper',
            )}
          >
            {/* `flex-1` below `sm` turns three labels into three equal
                full-width targets — 44px tall by a third of the screen wide —
                instead of three 40x36px words huddled in the left corner,
                which is a reach and a miss on a phone held in one hand. */}
            <TabsList className="max-sm:px-4">
              <TabsTrigger value="transcript" className="max-sm:h-11 max-sm:flex-1">
                Transcript
                {view.transcript.word_count ? (
                  <span className="ml-2 text-[0.6875rem] tabular text-ink-dim">
                    {view.transcript.word_count}
                  </span>
                ) : null}
              </TabsTrigger>
              <TabsTrigger value="insights" className="max-sm:h-11 max-sm:flex-1">
                Insights
              </TabsTrigger>
              <TabsTrigger value="files" className="max-sm:h-11 max-sm:flex-1">
                Files
                {view.artifacts.length > 0 ? (
                  <span className="ml-2 text-[0.6875rem] tabular text-ink-dim">
                    {view.artifacts.length}
                  </span>
                ) : null}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* -- transcript --------------------------------------------------- */}
          <TabsContent value="transcript" className="max-sm:pt-4">
            {transcript.status === 'ready' ? (
              <TranscriptPanel
                document={transcript.document}
                roster={roster}
                player={documentPlayer}
                focusIdentity={focusIdentity}
                onFocusIdentity={setFocusIdentity}
              />
            ) : transcript.status === 'error' ? (
              <Notice tone="critical" title="The transcript could not be read">
                {transcript.error.message}
              </Notice>
            ) : transcript.status === 'loading' ? (
              <PendingBlock title="Loading the transcript" reason="Fetching the document." />
            ) : (
              <PendingBlock
                title={
                  view.recording_ever_enabled === false
                    ? 'There is no transcript for this call'
                    : 'No transcript yet'
                }
                // The server's own reason once there is a document to ask
                // about; before that there is nothing to ask and the honest
                // answer is the pipeline's state.
                reason={
                  view.transcript.artifact_id
                    ? transcript.reason
                    : view.recording_ever_enabled === false
                      ? 'Audio capture was never enabled, so there are no stored bytes to transcribe.'
                      : `The audio pipeline is ${view.pipeline.state ?? 'queued'}. The transcript appears here when it finishes, and this page rechecks by itself.`
                }
                icon={<FileText />}
              />
            )}
          </TabsContent>

          {/* -- insights ----------------------------------------------------- */}
          <TabsContent value="insights" className="max-sm:pt-4">
            <div className="space-y-5">
              {view.metrics ? (
                <InsightsPanel
                  metrics={view.metrics}
                  roster={roster}
                  transcript={transcriptDocument}
                  lanes={lanes}
                  player={documentPlayer}
                  timelineDurationMs={timelineDurationMs}
                  roomDurationMs={view.duration_ms}
                />
              ) : (
                <PendingBlock
                  title="No conversation metrics yet"
                  reason={
                    view.recording_ever_enabled === false
                      ? 'Metrics are derived from stored audio, and capture was never enabled for this call.'
                      : 'Talk share, turn-taking and silence are computed at the end of the audio pipeline, with the transcript.'
                  }
                />
              )}
              <MediaQualityPanel quality={view.media_quality} roster={roster} />
            </div>
          </TabsContent>

          {/* -- files -------------------------------------------------------- */}
          <TabsContent value="files" className="max-sm:pt-4">
            <FilesPanel
              sessionId={sessionId}
              artifacts={view.artifacts}
              video={videoState}
              roster={roster}
            />
          </TabsContent>
        </Tabs>
      </div>

      <p className="border-t border-line pt-5 text-xs text-ink-dim">
        {view.from_cache && !view.upstream_unavailable
          ? // Served from SQLite because the session is finished and its snapshot
            // is final. That is the system working, not a degradation, so it is
            // a footnote rather than a banner.
            `Served from this app's catalog · last confirmed with the control plane ${formatRelative(view.fetched_at)}`
          : `Last refreshed ${formatRelative(view.fetched_at)}`}
        {view.is_local ? '' : ' · this session was not created by this app, so it is read live and not cached'}
      </p>
    </div>
  )
}
